/**
 * Public DIY Templates Catalog
 *
 * Browse all available DIY project templates.
 * Anonymous users can view and start any template.
 * Links to /diy/$slug for personalization flow.
 */

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { convexQuery } from '@convex-dev/react-query'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '@mantine/hooks'
import { useState } from 'react'
import {
  Badge,
  Box,
  Card,
  Container,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import {
  IconArrowRight,
  IconClock,
  IconCurrencyDollar,
  IconSearch,
  IconSparkles,
  IconTool,
  IconWand,
} from '@tabler/icons-react'
import { api } from 'convex/_generated/api'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/diy/')({
  head: () => ({
    meta: seo({
      title: 'DIY Home Improvement Guides — Step-by-Step Project Templates | Anchor',
      description:
        'Free step-by-step DIY guides for home repair, plumbing, electrical, flooring, and more. Personalized project plans with tool lists, cost estimates, and tips — no signup required.',
      keywords: 'DIY home improvement, home repair guides, how to fix, plumbing DIY, electrical DIY, flooring installation, bathroom tile, home maintenance',
    }),
  }),
  loader: async ({ context }) => {
    // Prefetch featured templates for SSR
    await context.queryClient.ensureQueryData(
      convexQuery(api.diy.templates.getFeaturedTemplates, { limit: 12 })
    )
  },
  component: DiyIndexPage,
})

// Difficulty color mapping
const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'red',
}

// Category display names
const CATEGORY_LABELS: Record<string, string> = {
  drywall: 'Drywall',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  painting: 'Painting',
  flooring: 'Flooring',
  carpentry: 'Carpentry',
  roofing: 'Roofing',
  hvac: 'HVAC',
  landscaping: 'Landscaping',
  appliances: 'Appliances',
  bathroom: 'Bathroom',
}

interface Template {
  _id: string
  slug: string
  title: string
  description: string
  category: string
  difficulty: string
  estimatedHours?: number
  estimatedCostMin?: number
  estimatedCostMax?: number
  tags?: Array<string>
}

function TemplateCard({ template }: { template: Template }) {
  return (
    <Link
      to="/diy/$slug"
      params={{ slug: template.slug }}
      style={{ textDecoration: 'none' }}
    >
      <Card
        withBorder
        padding="lg"
        radius="md"
        style={{ cursor: 'pointer', height: '100%' }}
      >
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <Box style={{ flex: 1 }}>
              <Text fw={600} size="md" lineClamp={2}>
                {template.title}
              </Text>
            </Box>
            <Badge
              variant="light"
              color={DIFFICULTY_COLORS[template.difficulty] ?? 'gray'}
              size="sm"
            >
              {template.difficulty}
            </Badge>
          </Group>

          <Text size="sm" c="dimmed" lineClamp={2}>
            {template.description}
          </Text>

          <Group gap="md" wrap="wrap">
            <Badge variant="outline" size="xs" color="gray">
              {CATEGORY_LABELS[template.category] ?? template.category}
            </Badge>

            {template.estimatedHours && (
              <Group gap={4}>
                <IconClock size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {template.estimatedHours}h
                </Text>
              </Group>
            )}

            {(template.estimatedCostMin || template.estimatedCostMax) && (
              <Group gap={4}>
                <IconCurrencyDollar size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  ${template.estimatedCostMin ?? 0} - ${template.estimatedCostMax ?? template.estimatedCostMin ?? 0}
                </Text>
              </Group>
            )}
          </Group>

          <Group justify="flex-end">
            <Text size="xs" c="primary" fw={500}>
              Start Project <IconArrowRight size={12} style={{ verticalAlign: 'middle' }} />
            </Text>
          </Group>
        </Stack>
      </Card>
    </Link>
  )
}

function DiyIndexPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)

  // Featured templates (always shown)
  const { data: featuredTemplates } = useSuspenseQuery(
    convexQuery(api.diy.templates.getFeaturedTemplates, { limit: 12 })
  )

  // Search results (only when searching)
  const { data: searchResults, isLoading: isSearching } = useQuery({
    ...convexQuery(api.diy.templates.searchTemplates, {
      searchTerm: debouncedSearch,
      limit: 20,
    }),
    enabled: debouncedSearch.length >= 2,
  })

  const isSearchActive = debouncedSearch.length >= 2
  // Use search results when searching, otherwise featured templates
  // searchResults may be undefined when not enabled
  const displayTemplates = isSearchActive ? (searchResults ?? []) : featuredTemplates

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Box ta="center">
          <Group justify="center" gap="sm" mb="xs">
            <ThemeIcon size="lg" variant="light" color="primary">
              <IconTool size={20} />
            </ThemeIcon>
            <Title order={1}>DIY Project Templates</Title>
          </Group>
          <Text c="dimmed" size="lg" maw={600} mx="auto">
            Browse our collection of step-by-step DIY guides. Get a personalized plan
            tailored to your skill level and tools.
          </Text>
        </Box>

        {/* Search */}
        <TextInput
          placeholder="Search projects (e.g., 'faucet', 'drywall', 'paint')..."
          leftSection={<IconSearch size={16} />}
          size="md"
          radius="md"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          styles={{
            input: { minHeight: 48 },
          }}
        />

        {/* Feature cards */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card withBorder radius="md" p="md">
            <Group gap="md" wrap="nowrap">
              <ThemeIcon size="xl" variant="light" color="primary" radius="xl">
                <IconSparkles size={24} />
              </ThemeIcon>
              <Box>
                <Text fw={600}>Personalized Plans</Text>
                <Text size="sm" c="dimmed">
                  Each template adapts to your skill level, tools, and situation — answer a few questions to get started.
                </Text>
              </Box>
            </Group>
          </Card>

          <Card
            withBorder
            radius="md"
            p="md"
            component={Link}
            to="/diy/create"
            style={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            <Group gap="md" wrap="nowrap" justify="space-between">
              <Group gap="md" wrap="nowrap">
                <ThemeIcon size="xl" variant="light" color="violet" radius="xl">
                  <IconWand size={24} />
                </ThemeIcon>
                <Box>
                  <Text fw={600}>Don't see your project?</Text>
                  <Text size="sm" c="dimmed">
                    Describe any home project and get a custom step-by-step plan — no account required.
                  </Text>
                </Box>
              </Group>
              <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
            </Group>
          </Card>
        </SimpleGrid>

        {/* Templates Grid */}
        <Box>
          <Group justify="space-between" mb="md">
            <Title order={3}>
              {isSearchActive
                ? `Search Results (${displayTemplates.length})`
                : 'Featured Projects'}
            </Title>
          </Group>

          {isSearching ? (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={180} radius="md" />
              ))}
            </SimpleGrid>
          ) : displayTemplates.length > 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {displayTemplates.map((template) => (
                <TemplateCard key={template._id} template={template} />
              ))}
            </SimpleGrid>
          ) : isSearchActive ? (
            <Card withBorder p="xl" ta="center">
              <Text c="dimmed">
                No projects found for "{debouncedSearch}". Try a different search term.
              </Text>
            </Card>
          ) : (
            <Card withBorder p="xl" ta="center">
              <Text c="dimmed">
                No featured templates available yet. Check back soon!
              </Text>
            </Card>
          )}
        </Box>
      </Stack>
    </Container>
  )
}
